/**
 * The IANA time zone catalog a warehouse's clock can be set to, and how each
 * one reads in the picker.
 *
 * `Intl.supportedValuesOf("timeZone")` is the zone list itself -- there is no
 * separate BFC list to keep in sync with tzdata, and it is the same catalog
 * every `Intl.DateTimeFormat` zone lookup already trusts.
 */

export interface TimezoneOption {
  /** IANA zone name, e.g. `"America/New_York"` -- what a warehouse stores. */
  id: string;
  /** e.g. `"(UTC-4) America/New York"` -- what the picker shows for it. */
  label: string;
}

/** `zone`'s current offset from UTC in minutes, e.g. `-240`, `330`, `0`. */
function offsetMinutes(zone: string, at: Date): number {
  const raw = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "longOffset",
  })
    .formatToParts(at)
    .find((part) => part.type === "timeZoneName")?.value;

  const match = raw?.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match) return 0;

  const [, sign, hours, minutes] = match;
  const magnitude = Number(hours) * 60 + Number(minutes);
  return sign === "-" ? -magnitude : magnitude;
}

/** e.g. `("America/New_York", now)` -> `"(UTC-4) America/New York"`. */
export function formatTimezoneLabel(
  zone: string,
  at: Date = new Date(),
): string {
  const raw = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(at)
    .find((part) => part.type === "timeZoneName")?.value;

  const offset = (raw ?? "GMT").replace("GMT", "UTC");
  return `(${offset}) ${zone.replace(/_/g, " ")}`;
}

/**
 * Every IANA zone, labelled and sorted west-to-east by current offset (then
 * alphabetically within a tie) -- the order a time zone picker usually lists
 * them in. Computed once at import time: DST can shift a zone's offset by an
 * hour, but never far enough to change where it belongs in this ordering.
 */
export function timezoneOptions(at: Date = new Date()): TimezoneOption[] {
  return Intl.supportedValuesOf("timeZone")
    .map((id) => ({
      id,
      label: formatTimezoneLabel(id, at),
      offset: offsetMinutes(id, at),
    }))
    .sort((a, b) => a.offset - b.offset || a.label.localeCompare(b.label))
    .map(({ id, label }) => ({ id, label }));
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = timezoneOptions();

/**
 * Whether `option` matches what has been typed toward it so far, on its
 * label or its raw IANA id -- a manager might type either "new york" or
 * "america/new_york".
 */
export function matchesTimezoneQuery(
  option: TimezoneOption,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return (
    option.label.toLowerCase().includes(needle) ||
    option.id.toLowerCase().includes(needle)
  );
}

/** `options`, narrowed to what `query` matches -- what the box actually renders. */
export function filterTimezoneOptions(
  options: ReadonlyArray<TimezoneOption>,
  query: string,
): TimezoneOption[] {
  return options.filter((option) => matchesTimezoneQuery(option, query));
}
